import React, { useState, useEffect, useCallback } from 'react';
import { useStringsContext } from '../../../../contexts/strings';
import { SxStyle } from '../../../../types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { SUPPORTED_LEAGUES, TeamPermissions, SuperAdminUser, ROLES } from '../../../../types';
import Typography from '@mui/material/Typography';
import EditRoleAccordion from './edit-role-accordion';
import {
  convertPermissionsTeam,
  getTeamPermissionsToastMessage,
  removeUneditedChanges,
  fetchBackendUserTeam,
} from '../helpers';
import { PermissionsTeam, TeamMinimized } from '../types';
import { APP_TYPE } from '../../../../types/app-config';
import isEmpty from 'lodash/isEmpty';
import { useSnackbar } from 'notistack';
import { AuthContextValues, useUserAuth } from '../../../../contexts/auth';
import adminApi from '../../../../api/admin-api';

const styles: SxStyle = {
  email: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  appTeams: TeamMinimized[];
  editUser: SuperAdminUser;
  currentUser: AuthContextValues;
}

type TeamPermissionsPerLeague = Record<string, PermissionsTeam>;
type TeamsPerLeague = Record<string, TeamMinimized[]>;

const EditUserDialog = ({ isOpen, onClose, appTeams, editUser, currentUser }: Props) => {
  const { strings } = useStringsContext();
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [permissionsPerLeague, setPermissionsPerLeague] = useState<TeamPermissionsPerLeague>();
  const [teamsPerLeague, setTeamsPerLeague] = useState<TeamsPerLeague>();
  const leagues = [...Object.values(SUPPORTED_LEAGUES), 'league'];
  const { currentUser: userAuth } = useUserAuth();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (appTeams) {
      const newTeamsPerLeague = appTeams
        .filter(
          ({ abbrv }) => currentUser.hasTeamRole(abbrv, ROLES.ADMIN) || currentUser.isSuperAdmin
        )
        .reduce((acc, team) => {
          const league = team?.appType === APP_TYPE.CLUB ? team?.league : 'league';
          return {
            ...acc,
            [league]: [...((acc[league] && acc[league]) || []), team],
          };
        }, {} as TeamsPerLeague);

      const newPermissionsPerLeague = Object.entries(editUser.teams).reduce(
        (permissionsTeam, [abbrv, role]) => {
          const foundTeam = appTeams.find((team) => team.abbrv === abbrv);
          const type =
            foundTeam && (foundTeam.appType === APP_TYPE.CLUB ? foundTeam.league : 'league');
          const previousAbbrvs =
            (type && permissionsTeam[type] && permissionsTeam[type][role]) || [];
          return type
            ? {
                ...permissionsTeam,
                [type]: {
                  ...permissionsTeam[type],
                  [role]: [...previousAbbrvs, abbrv],
                },
              }
            : permissionsTeam;
        },
        {} as TeamPermissionsPerLeague
      );

      setPermissionsPerLeague(newPermissionsPerLeague);
      setTeamsPerLeague(newTeamsPerLeague);
    }
  }, [appTeams, currentUser, editUser]);

  const handleChangePerLeague = useCallback(
    (league: string, newPermissionsTeam: PermissionsTeam) => {
      setPermissionsPerLeague((previousPermissionsPerLeague) => {
        return {
          ...previousPermissionsPerLeague,
          [league]: newPermissionsTeam,
        };
      });
      setIsDirty(true);
    },
    []
  );

  const handleSubmit = async () => {
    setIsSubmitting(true);

    let newTeamPermissions = {} as TeamPermissions;

    Object.entries(permissionsPerLeague!).forEach(([, permissionsTeam]) => {
      newTeamPermissions = {
        ...newTeamPermissions,
        ...convertPermissionsTeam(permissionsTeam),
      };
    });

    const initialTeamPermissions = await fetchBackendUserTeam(editUser.uid);

    const editedTeams = removeUneditedChanges(initialTeamPermissions, newTeamPermissions);

    if (isEmpty(editedTeams)) {
      return;
    }

    try {
      const response = await adminApi.post({
        path: `/users/update-user-teams`,
        auth: await userAuth?.getIdToken(),
        body: {
          teams: editedTeams,
          userId: editUser.uid,
        },
      });
      const responseData = await response.json();

      // it will delete the selected-user if the calling-user removed their last team, unless user is a superadmin
      if (isEmpty(responseData?.teams) && !editUser.superadmin) {
        enqueueSnackbar(strings.usersAndPermissions.noTeamsLeft, {
          variant: 'info',
          autoHideDuration: 10000,
        });
      }

      const toastMessage = getTeamPermissionsToastMessage(editedTeams);
      const isError = responseData.error;
      enqueueSnackbar(toastMessage, {
        variant: isError ? 'error' : 'success',
      });

      setIsSubmitting(false);

      if (!isError) {
        onClose();
      }
    } catch (error) {
      setIsSubmitting(false);
      enqueueSnackbar(strings.messages.error, { variant: 'error' });
    }
  };

  return permissionsPerLeague && teamsPerLeague ? (
    <Dialog open={isOpen} onClose={onClose} scroll="paper" fullWidth>
      <DialogTitle>
        {strings.usersAndPermissions.editTeamPermissions}
        <Typography variant="body1" sx={styles.email}>
          {editUser.email}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {leagues.map((league) => (
          <EditRoleAccordion
            key={league}
            title={league === 'league' ? strings.usersAndPermissions.leagueApps : league}
            permissionsTeam={permissionsPerLeague[league]}
            teams={teamsPerLeague[league]}
            onChange={(newPermissionsTeam) => handleChangePerLeague(league, newPermissionsTeam)}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {strings.actions.cancel}
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isDirty}
          loading={isSubmitting}
        >
          {strings.actions.save}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  ) : (
    <></>
  );
};

export default EditUserDialog;
